/**
 * Dynamic Connection Service
 *
 * Service for handling advanced connection features:
 * - Dynamic Connections (oBO/On-Behalf-Of)
 * - Connection Runtime URLs for Standard SKU
 * - Gateway Support for on-premises connections
 * - Managed Identity authentication
 * - Duplicate connection detection
 *
 * Based on LogicAppsUX patterns from libs/designer.
 */

import { getArmBaseUrl } from './armConfig';
import { apiVersions, getLogicEndpoint } from './endpoints';
import type {
  ConnectionReference,
  ConnectionReferences,
  ConnectionFeatureType,
  ConnectionRuntimeSource,
  ApiHubAuthentication,
} from '@/types/connectionReference';
import type { ConnectionParameterSet } from '@/types/connectionParameters';
import { hasManagedIdentityParameter } from '@/types/connectionParameters';
import type { AzureResourceContext } from './connectionApi';

// =============================================================================
// TYPES
// =============================================================================

/**
 * SKU type for Logic App workflows
 */
export const LogicAppSku = {
  /** Consumption SKU - serverless */
  Consumption: 'Consumption',
  /** Standard SKU - dedicated hosting */
  Standard: 'Standard',
} as const;
export type LogicAppSku = (typeof LogicAppSku)[keyof typeof LogicAppSku];

/**
 * Workflow information needed for connection resolution
 */
export interface WorkflowInfo {
  /** Logic App SKU type */
  sku: LogicAppSku;
  /** Runtime URL for Standard SKU Logic Apps */
  runtimeUrl?: string;
  /** Workflow name */
  workflowName: string;
  /** Azure subscription ID */
  subscriptionId: string;
  /** Resource group */
  resourceGroup: string;
  /** Logic App name */
  logicAppName: string;
}

/**
 * Gateway information for on-premises connections
 */
export interface GatewayInfo {
  /** Gateway resource ID */
  gatewayId: string;
  /** Gateway display name */
  gatewayName: string;
  /** Gateway region */
  region: string;
  /** Gateway resource group */
  resourceGroup?: string;
}

/**
 * Managed identity configuration
 */
export interface ManagedIdentityConfig {
  /** Identity type */
  identityType: 'SystemAssigned' | 'UserAssigned';
  /** User-assigned identity resource ID (required for UserAssigned) */
  userAssignedIdentityId?: string;
  /** Audience URI for token acquisition */
  audience?: string;
}

/**
 * Dynamic connection keys response from Azure API
 */
export interface DynamicConnectionKeys {
  /** Connection keys for runtime access */
  connectionKey?: string;
  /** Runtime URLs */
  runtimeUrls?: string[];
  /** Additional properties */
  properties?: Record<string, unknown>;
}

/**
 * Connection creation info with dynamic features
 */
export interface ConnectionCreationInfo {
  /** Connection display name */
  displayName: string;
  /** Connection parameters */
  connectionParameters?: Record<string, unknown>;
  /** Connection parameter set values (for multi-auth) */
  connectionParametersSet?: {
    name: string;
    values: Record<string, unknown>;
  };
  /** Alternative parameter values */
  alternativeParameterValues?: Record<string, unknown>;
  /** Additional parameter values */
  additionalParameterValues?: Record<string, unknown>;
  /** Feature type (e.g., 'DynamicUserInvoked' for oBO) */
  features?: ConnectionFeatureType;
  /** Gateway configuration */
  gateway?: GatewayInfo;
  /** Managed identity configuration */
  managedIdentity?: ManagedIdentityConfig;
}

/**
 * Connection with dynamic features
 */
export interface DynamicConnection {
  /** Connection ID */
  id: string;
  /** Connection name */
  name: string;
  /** Connection properties */
  properties: {
    /** Display name */
    displayName?: string;
    /** API/Connector ID */
    api: { id: string; name?: string; displayName?: string };
    /** Connection status */
    statuses?: Array<{ status?: string; error?: { code?: string; message?: string } }>;
    /** Feature type for dynamic connections */
    features?: string;
    /** Proxy URL for dynamic connection calls */
    dynamicConnectionProxyUrl?: string;
    /** Test links for connection validation */
    testLinks?: Array<{ requestUri: string; method: string }>;
    /** Gateway configuration */
    gateway?: {
      id: string;
      name: string;
      type: string;
    };
    /** Parameter values */
    parameterValues?: Record<string, unknown>;
    /** Connection runtime URL */
    connectionRuntimeUrl?: string;
  };
}

// =============================================================================
// DYNAMIC CONNECTION DETECTION
// =============================================================================

/**
 * Check if a connection has dynamic (oBO) features enabled
 * Matches LogicAppsUX isDynamicConnection utility
 */
export function isDynamicConnection(features?: string): boolean {
  if (!features) return false;
  return features.toLowerCase() === 'dynamicuserinvoked';
}

/**
 * Check if a connection reference is using dynamic runtime
 */
export function isConnectionDynamic(reference: ConnectionReference): boolean {
  return reference.runtimeSource === 'Dynamic';
}

/**
 * Filter out dynamic connections from a list
 * Used to hide dynamic connections in non-Agent contexts
 */
export function filterDynamicConnections(
  connections: DynamicConnection[],
  includeDynamic: boolean = false
): DynamicConnection[] {
  if (includeDynamic) {
    return connections;
  }
  return connections.filter((conn) => !isDynamicConnection(conn.properties.features));
}

// =============================================================================
// CONNECTION RUNTIME URL
// =============================================================================

/**
 * Get the connection runtime URL based on SKU and connection info
 * For Standard SKU, constructs the runtime URL from workflow info
 * For Consumption SKU, uses the connection's own runtime URL
 */
export function getConnectionRuntimeUrl(
  connection: ConnectionReference,
  workflowInfo: WorkflowInfo
): string {
  // Standard SKU: Construct runtime URL from Logic App runtime endpoint
  if (workflowInfo.sku === LogicAppSku.Standard) {
    const baseUrl = workflowInfo.runtimeUrl;
    if (baseUrl && connection.connectionName) {
      // Format: {runtimeUrl}/connections/{connectionName}
      const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      return `${normalizedBase}/connections/${connection.connectionName}`;
    }
  }

  // Consumption SKU or fallback: Use the connection's own runtime URL
  return connection.connectionRuntimeUrl ?? '';
}

/**
 * Build the connection reference with proper runtime URL
 */
export function buildConnectionReference(
  connection: DynamicConnection,
  workflowInfo: WorkflowInfo,
  options?: {
    useDynamicConnection?: boolean;
  }
): ConnectionReference {
  const reference: ConnectionReference = {
    api: { id: connection.properties.api.id },
    connection: { id: connection.id },
    connectionName: connection.name,
  };

  // Set runtime URL
  const runtimeUrl = getConnectionRuntimeUrl(reference, workflowInfo);
  if (runtimeUrl) {
    reference.connectionRuntimeUrl = runtimeUrl;
  }

  // Handle dynamic connections
  if (options?.useDynamicConnection || isDynamicConnection(connection.properties.features)) {
    reference.runtimeSource = 'Dynamic';
    if (connection.properties.dynamicConnectionProxyUrl) {
      reference.dynamicConnectionProxyUrl = connection.properties.dynamicConnectionProxyUrl;
    }
  }

  return reference;
}

// =============================================================================
// DYNAMIC CONNECTION KEYS (oBO)
// =============================================================================

/**
 * Fetch dynamic connection keys using the listDynamicConnectionKeys API
 * Used for On-Behalf-Of (oBO) authentication flows
 */
export async function listDynamicConnectionKeys(
  connectionId: string,
  context: AzureResourceContext
): Promise<DynamicConnectionKeys | null> {
  const apiVersion = '2015-08-01-preview';
  const url = `${getArmBaseUrl()}${connectionId}/listDynamicConnectionKeys?api-version=${apiVersion}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${context.accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.warn(`Failed to list dynamic connection keys: ${response.status}`);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.warn('Error listing dynamic connection keys:', error);
    return null;
  }
}

/**
 * Fetch OBO data for a workflow with dynamic connections
 * Searches for connections with runtimeSource: 'Dynamic' and fetches their keys
 */
export async function fetchOBOData(
  connectionReferences: ConnectionReferences,
  context: AzureResourceContext
): Promise<DynamicConnectionKeys | null> {
  // Find the first dynamic connection
  for (const [, reference] of Object.entries(connectionReferences)) {
    if (isConnectionDynamic(reference)) {
      return listDynamicConnectionKeys(reference.connection.id, context);
    }
  }
  return null;
}

// =============================================================================
// GATEWAY SUPPORT
// =============================================================================

/**
 * List available gateways for a subscription
 */
export async function listGateways(
  context: AzureResourceContext
): Promise<GatewayInfo[]> {
  const apiVersion = '2016-06-01';
  const url = `${getArmBaseUrl()}/subscriptions/${context.subscriptionId}/providers/Microsoft.Web/connectionGateways?api-version=${apiVersion}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${context.accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to list gateways: ${response.status}`);
    }

    const data = await response.json();
    const gateways: GatewayInfo[] = [];

    for (const gateway of data.value || []) {
      gateways.push({
        gatewayId: gateway.id,
        gatewayName: gateway.name,
        region: gateway.location,
        resourceGroup: gateway.properties?.resourceGroup,
      });
    }

    return gateways;
  } catch (error) {
    console.error('Error listing gateways:', error);
    return [];
  }
}

/**
 * Check if a connector requires a gateway
 */
export function requiresGateway(connectorProperties: Record<string, unknown>): boolean {
  // Check for gateway requirement in connection parameters
  const connectionParameters = connectorProperties.connectionParameters as Record<string, unknown> | undefined;
  if (connectionParameters) {
    for (const param of Object.values(connectionParameters)) {
      if (typeof param === 'object' && param !== null) {
        const paramObj = param as { type?: string };
        if (paramObj.type === 'gatewaySetting') {
          return true;
        }
      }
    }
  }
  return false;
}

// =============================================================================
// MANAGED IDENTITY SUPPORT
// =============================================================================

/**
 * Check if a connector supports managed identity authentication
 */
export function supportsManagedIdentity(connectorProperties: Record<string, unknown>): boolean {
  const connectionParameters = connectorProperties.connectionParameters as Record<string, unknown> | undefined;
  if (connectionParameters) {
    for (const param of Object.values(connectionParameters)) {
      if (typeof param === 'object' && param !== null) {
        const paramObj = param as { type?: string };
        if (paramObj.type === 'managedIdentity') {
          return true;
        }
      }
    }
  }

  const parameterSets = connectorProperties.connectionParameterSets as { values?: unknown[] } | undefined;
  if (parameterSets?.values) {
    for (const set of parameterSets.values as ConnectionParameterSet[]) {
      if (set && hasManagedIdentityParameter(set)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Build authentication object for managed identity
 */
export function buildManagedIdentityAuth(config: ManagedIdentityConfig): {
  type: string;
  identity?: string;
} {
  const auth: { type: string; identity?: string } = {
    type: 'ManagedServiceIdentity',
  };

  if (config.identityType === 'UserAssigned' && config.userAssignedIdentityId) {
    auth.identity = config.userAssignedIdentityId;
  }

  return auth;
}

/**
 * `properties` envelope for an MI-authenticated call to the Otto Cloud
 * app-scoped `/dynamic/dynamicInvoke` endpoint. The Cloud validator
 * deserialises this as `RawCallProperties` (LogicAppsUX/Edge shape) —
 * `workflowReference` is NOT used on this route.
 *
 * Module-private: callers go through {@link postManagedApiDynamicInvoke}.
 */
interface DynamicInvokeMiProperties {
  connection: { id: string };
  connectionRuntimeUrl?: string;
  connectionProperties?: Record<string, unknown>;
  authentication: { type: 'ManagedServiceIdentity'; identity?: string };
}

/**
 * Extract the MI token audience that the api-hub needs as the `aud` claim
 * when acquiring the downstream token. Every MI-capable connector publishes
 * it at `connectionParameterSets.values[*].parameters[*].managedIdentitySettings.resourceUri`.
 *
 * When `parameterSetName` is given (the auth method the connection was
 * created with) we look in that set first and fall back to any other set.
 * Returns undefined when the connector doesn't advertise MI.
 */
export function extractMiAudienceFromConnector(
  connector: unknown,
  parameterSetName?: string
): string | undefined {
  const sets = (connector as { properties?: { connectionParameterSets?: { values?: unknown[] } } })
    ?.properties?.connectionParameterSets?.values;
  if (!Array.isArray(sets)) return undefined;
  return extractMiAudienceFromParameterSets(sets as ConnectionParameterSet[], parameterSetName);
}

/** Variant that takes the already-unwrapped sets (e.g. from a manifest). */
export function extractMiAudienceFromParameterSets(
  sets: ConnectionParameterSet[] | undefined,
  parameterSetName?: string
): string | undefined {
  if (!sets?.length) return undefined;
  const findInSet = (set: ConnectionParameterSet | undefined): string | undefined => {
    for (const param of Object.values(set?.parameters ?? {})) {
      const uri = param?.managedIdentitySettings?.resourceUri;
      if (typeof uri === 'string' && uri.length > 0) return uri;
    }
    return undefined;
  };
  if (parameterSetName) {
    const target = sets.find((s) => s?.name?.toLowerCase() === parameterSetName.toLowerCase());
    const fromTarget = findInSet(target);
    if (fromTarget) return fromTarget;
  }
  for (const set of sets) {
    const uri = findInSet(set);
    if (uri) return uri;
  }
  return undefined;
}

/**
 * Read the persisted `connectionProperties.authentication` block. Centralizes
 * the narrow typecast so callers don't peek into the unknown shape.
 */
function readPersistedAuth(
  connectionProperties: Record<string, unknown> | undefined
): { type?: string; audience?: string } | undefined {
  const raw = (connectionProperties as { authentication?: unknown } | undefined)?.authentication;
  if (!raw || typeof raw !== 'object') return undefined;
  const { type, audience } = raw as { type?: unknown; audience?: unknown };
  return {
    ...(typeof type === 'string' ? { type } : {}),
    ...(typeof audience === 'string' && audience.length > 0 ? { audience } : {}),
  };
}

/** Audience persisted on `connectionProperties.authentication.audience`, if any. */
export function readPersistedMiAudience(
  connectionProperties: Record<string, unknown> | undefined
): string | undefined {
  return readPersistedAuth(connectionProperties)?.audience;
}

// =============================================================================
// MANAGED IDENTITY CONNECTION DETECTION
// =============================================================================
// Mirrors LogicAppsUX `libs/designer-v2/src/lib/core/utils/connectors/connections.ts:267-294`.

const ALT_PARAMETER_VALUE_TYPE = 'Alternative';
const MI_PARAMETER_TYPE = 'managedIdentity';

type ConnectorWithSets = {
  properties?: {
    connectionParameterSets?: {
      values?: Array<{ name?: string; parameters?: Record<string, { type?: string }> }>;
    };
  };
};
type ConnectionWithAuth = {
  properties?: {
    parameterValueSet?: { name?: string };
    parameterValueType?: string;
  };
};

/** Multi-auth MI: connection's parameterValueSet matches a connector set with an MI parameter. */
export function isConnectionMultiAuthManagedIdentity(
  connection: unknown,
  connector: unknown
): boolean {
  const setName = (connection as ConnectionWithAuth)?.properties?.parameterValueSet?.name;
  const sets = (connector as ConnectorWithSets)?.properties?.connectionParameterSets?.values;
  if (!setName || !Array.isArray(sets)) return false;
  const target = sets.find((s) => s?.name === setName);
  return Object.values(target?.parameters ?? {}).some((p) => p?.type === MI_PARAMETER_TYPE);
}

/** Single-auth MI: parameterValueType === 'Alternative' with no parameterValueSet. */
export function isConnectionSingleAuthManagedIdentity(connection: unknown): boolean {
  const props = (connection as ConnectionWithAuth)?.properties;
  return props?.parameterValueType === ALT_PARAMETER_VALUE_TYPE && !props?.parameterValueSet;
}

/** True iff the connection authenticates via managed identity. */
export function isManagedIdentityConnection(connection: unknown, connector: unknown): boolean {
  return (
    isConnectionSingleAuthManagedIdentity(connection) ||
    isConnectionMultiAuthManagedIdentity(connection, connector)
  );
}

/**
 * Build the MI envelope's `properties`, or undefined to route via the bare
 * per-connection path. Callers classify via `useManagedConnectionInfo` and
 * pass `isManagedIdentity` + the live `connectionRuntimeUrl` here.
 *
 * Otto BE shape (verified 2026-06-04; identical to Logic Apps Standard
 * runtime `dynamicInvoke` per LogicAppsUX `getManagedIdentityRequestProperties`):
 *   {
 *     connection: { id },
 *     connectionRuntimeUrl?,
 *     connectionProperties: { authentication: { type, audience } },
 *     authentication: { type: 'ManagedServiceIdentity', identity? }
 *   }
 *
 * `connectionProperties` here only accepts `{ authentication }` — extra fields
 * (e.g. SB `namespaceEndpoint`) are rejected by the Otto Cloud validator.
 */
function buildMiProperties(
  reference: ConnectionReference,
  audience: string | undefined,
  isManagedIdentity: boolean,
  connectionRuntimeUrl: string | undefined
): DynamicInvokeMiProperties | undefined {
  if (!isManagedIdentity) return undefined;

  const resolvedAudience = readPersistedMiAudience(reference.connectionProperties) ?? audience;
  if (!resolvedAudience) return undefined;

  const properties: DynamicInvokeMiProperties = {
    connection: { id: reference.connection.id },
    connectionProperties: {
      authentication: { type: 'ManagedServiceIdentity', audience: resolvedAudience },
    },
    authentication: { type: 'ManagedServiceIdentity' },
  };
  if (connectionRuntimeUrl) {
    properties.connectionRuntimeUrl = connectionRuntimeUrl;
  }
  if (reference.authentication?.identity) {
    properties.authentication.identity = reference.authentication.identity;
  }

  return properties;
}

/** Parse `{project, app}` from an Otto-scoped connection ID. */
function parseOttoAppScope(
  connectionId: string
): { projectName: string; appName: string } | null {
  const match = connectionId.match(
    /\/automationProjects\/([^/]+)\/applications\/([^/]+)\/connections\//i
  );
  if (!match) return null;
  const [, projectName, appName] = match;
  if (!projectName || !appName) return null;
  return { projectName, appName };
}

/**
 * Options for {@link postManagedApiDynamicInvoke}.
 */
export interface ManagedApiDynamicInvokeOptions {
  connectionReference: ConnectionReference;
  /** The `request: { method, path, ... }` envelope built from the operation swagger. */
  request: unknown;
  /** Otto data-plane bearer token. Sending an ARM token causes `MisMatchingOAuthClaims`. */
  accessToken: string;
  /** MI token audience (from `useMiAudience`). Required only when `isManagedIdentity` is true. */
  miAudience?: string | undefined;
  /** Whether the connection authenticates via MI (from `useManagedConnectionInfo`). */
  isManagedIdentity?: boolean | undefined;
  /** Live `connectionRuntimeUrl` from the connection ARM resource (from `useManagedConnectionInfo`). */
  connectionRuntimeUrl?: string | undefined;
  /** Caller label for log lines and error messages (e.g. `'list'`, `'schema'`, `'tree'`). */
  label: string;
}

/**
 * POST a managed-API dynamicInvoke request, picking the right route per auth type.
 *
 *  - **MI** → app-scoped `/automationProjects/{p}/applications/{a}/dynamic/dynamicInvoke`
 *    with the full `{ connection, connectionRuntimeUrl?, connectionProperties, authentication }`
 *    envelope (verified with Otto BE 2026-06-04; identical to Logic Apps Standard).
 *  - **Access-key / OAuth** → legacy per-connection `{connectionId}/dynamicInvoke`
 *    with a bare `{ request }` body.
 *
 * Throws on non-2xx with the response body included for diagnostics.
 */
export async function postManagedApiDynamicInvoke<T = unknown>(
  options: ManagedApiDynamicInvokeOptions
): Promise<T> {
  const { connectionReference, request, accessToken, miAudience, isManagedIdentity, connectionRuntimeUrl, label } = options;
  const connectionId = connectionReference.connection.id;
  const properties = buildMiProperties(connectionReference, miAudience, !!isManagedIdentity, connectionRuntimeUrl);

  let url: string;
  let body: Record<string, unknown>;
  if (properties) {
    const scope = parseOttoAppScope(connectionId);
    if (!scope) {
      throw new Error(
        `MI dynamicInvoke requires an Otto-scoped connection ID; got: ${connectionId}`
      );
    }
    url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(scope.projectName)}/applications/${encodeURIComponent(scope.appName)}/dynamic/dynamicInvoke?api-version=${apiVersions.otto}`;
    body = { request, properties };
  } else {
    url = `${getLogicEndpoint()}${connectionId}/dynamicInvoke?api-version=${apiVersions.otto}`;
    body = { request };
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Dynamic invoke (${label}) failed (${response.status}): ${errorText}`);
  }

  return response.json() as Promise<T>;
}

// =============================================================================
// DUPLICATE DETECTION
// =============================================================================

/**
 * Check if a connection with the same parameters already exists
 * Helps prevent creating duplicate connections
 */
export function findExistingConnection(
  connectorId: string,
  parameterValues: Record<string, unknown>,
  existingConnections: DynamicConnection[]
): DynamicConnection | null {
  for (const conn of existingConnections) {
    // Must be same connector
    if (!conn.properties.api.id.toLowerCase().includes(connectorId.toLowerCase())) {
      continue;
    }

    // Compare parameter values (excluding secrets)
    const existingParams = conn.properties.parameterValues ?? {};
    if (areParametersEquivalent(parameterValues, existingParams)) {
      return conn;
    }
  }

  return null;
}

/**
 * Compare parameter values for equivalence
 * Ignores secret values (which would be masked)
 */
function areParametersEquivalent(
  params1: Record<string, unknown>,
  params2: Record<string, unknown>
): boolean {
  // Get non-secret parameter keys
  const keys1 = Object.keys(params1).filter(k => !isSecretParameter(k, params1[k]));
  const keys2 = Object.keys(params2).filter(k => !isSecretParameter(k, params2[k]));

  // Must have same non-secret keys
  if (keys1.length !== keys2.length) {
    return false;
  }

  // Compare values
  for (const key of keys1) {
    if (!keys2.includes(key)) {
      return false;
    }
    if (JSON.stringify(params1[key]) !== JSON.stringify(params2[key])) {
      return false;
    }
  }

  return true;
}

/**
 * Check if a parameter looks like a secret
 */
function isSecretParameter(key: string, value: unknown): boolean {
  const secretIndicators = ['password', 'secret', 'key', 'token', 'credential', 'apikey'];
  const keyLower = key.toLowerCase();

  // Check key name
  for (const indicator of secretIndicators) {
    if (keyLower.includes(indicator)) {
      return true;
    }
  }

  // Check for masked values
  if (typeof value === 'string') {
    if (value.includes('***') || value === '[hidden]') {
      return true;
    }
  }

  return false;
}

// =============================================================================
// CONNECTION SERIALIZATION HELPERS
// =============================================================================

/**
 * Flatten connection reference for serialization
 * Moves dynamic connection properties to root level as per LogicAppsUX pattern
 */
export function flattenConnectionReferenceForSerialization(
  reference: ConnectionReference
): Record<string, unknown> {
  const result: Record<string, unknown> = {
    connectionId: reference.connection.id,
    connectionName: reference.connectionName,
    id: reference.api.id,
  };

  // Pull runtimeSource to root level for dynamic connections
  if (reference.runtimeSource) {
    result.runtimeSource = reference.runtimeSource;
  }

  // Include connectionProperties (excluding runtimeSource which was moved)
  if (reference.connectionProperties) {
    const props = { ...reference.connectionProperties };
    delete props.runtimeSource;
    if (Object.keys(props).length > 0) {
      result.connectionProperties = props;
    }
  }

  // Include authentication if present
  if (reference.authentication) {
    result.authentication = reference.authentication;
  }

  return result;
}

/**
 * Restore connection reference from serialized format
 */
export function restoreConnectionReferenceFromSerialized(
  serialized: Record<string, unknown>
): ConnectionReference {
  const reference: ConnectionReference = {
    api: { id: serialized.id as string },
    connection: { id: serialized.connectionId as string },
  };

  if (serialized.connectionName) {
    reference.connectionName = serialized.connectionName as string;
  }

  // Restore runtimeSource
  if (serialized.runtimeSource) {
    reference.runtimeSource = serialized.runtimeSource as ConnectionRuntimeSource;
  }

  // Restore connectionProperties
  if (serialized.connectionProperties) {
    reference.connectionProperties = serialized.connectionProperties as Record<string, unknown>;
  }

  // Restore authentication
  if (serialized.authentication) {
    reference.authentication = serialized.authentication as ApiHubAuthentication;
  }

  return reference;
}
